'use client';

import { STREAMING_EVENT_TYPES } from '../constants';
import type { StreamingEvent, StreamingHandlers } from '../types';
import type { VideoStudy } from '@/lib/reel-studio/agent/blocks';

import { PENDING_CONVERSATION, reelsTurnStore } from './reels-turn-store';

/**
 * Wraps a Reels turn's stream handlers so the turn is kept in the
 * `ReelsTurnStore`: the study the agent attached, and once the answer is
 * final, the question and the answer's text as the agent's next history.
 * Everything else passes through untouched.
 */
export function withReelsTurnRecording(
    handlers: StreamingHandlers,
    conversationId: string | null,
    prompt: string,
): StreamingHandlers {
    const id = conversationId ?? PENDING_CONVERSATION;
    let answer: string | null = null;
    let recorded = false;

    const record = () => {
        if (recorded || answer === null) return;
        recorded = true;
        reelsTurnStore.appendTurns(id, [
            { role: 'user', text: prompt },
            { role: 'assistant', text: answer },
        ]);
    };

    return {
        ...handlers,
        onMessage: (data: StreamingEvent) => {
            if (data.type === STREAMING_EVENT_TYPES.VIDEO_STUDY && data.content) {
                try {
                    reelsTurnStore.setStudy(id, JSON.parse(data.content) as VideoStudy);
                } catch {
                    // A malformed study is ignored; the turn still renders.
                }
            } else if (data.type === STREAMING_EVENT_TYPES.CONTENT_ARRAY && data.content) {
                try {
                    const items = JSON.parse(data.content) as Array<{
                        type: string;
                        content?: string;
                    }>;
                    answer = items
                        .filter((i) => i.type === STREAMING_EVENT_TYPES.TEXT && i.content)
                        .map((i) => i.content)
                        .join('\n\n');
                } catch {
                    answer = null;
                }
            } else if (data.type === STREAMING_EVENT_TYPES.DONE) {
                record();
            }
            handlers.onMessage?.(data);
        },
        onComplete: () => {
            record();
            handlers.onComplete?.();
        },
    };
}
